import { useState } from 'react';
import type { ReactNode } from 'react';
import { RectangleHorizontal, Settings, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import '@/components/tavolo/tavolo.css';
import { Button } from '@/components/common/Button';
import { PalcoScalato } from '@/components/tavolo/PalcoScalato';
import { TabelloneOrizzontale } from '@/components/tavolo/TabelloneOrizzontale';
import { cn } from '@/lib/cn';
import { conSegno } from '@/lib/qcp/resoconto';
import { senzaSchermo } from '@/lib/tavolo/schermo';
import { useOrientamento } from '@/lib/tavolo/useOrientamento';

/**
 * IL TAVOLO DEL CHINESE POKER nello stile del Burraco «premium» (fase 2 di `tma-orc-1-stile-cp`; tavole di qchinesepoker-orcb approvate da Luca, I-765):
 * UNA sola disposizione dei pezzi, in due modi. In PIEDI (390×844) una colonna; in ORIZZONTALE il PALCO 844×390 (l'avversario a sinistra, il mio tavolo al centro,
 * orologi, carte e tasti a destra) scalato alla larghezza che c'e, dentro il tabellone a schermo intero del modulo comune (`components/tavolo`).
 *
 * Come nel Burraco (`TavoloResponsivo`, #718): il tavolo parte SEMPRE in piedi, e il palco si apre con un TASTO (`data-azione="vista-orizzontale"`), non con la rotazione del
 * telefono: quella puo essere bloccata, e in una WebView a volte arriva girata. L'orientamento VERO del telefono decide solo se il palco aperto va girato di 90° per stare dritto.
 * Senza uno schermo (jsdom, un banco) resta il tavolo in piedi: e quello che i test conoscono.
 * ⛔ Solo DISPOSIZIONE: i dati, le mosse e i tocchi stanno nella rotta, che passa i pezzi gia fatti. Il marcatore `data-tavolo-cp` e di questo tavolo nuovo (0 nella build
 * di produzione, dove la sezione non c'e; 1 in `build:stg`).
 */
export type ModoTavolo = 'verticale' | 'palco';
export const PALCO_CP_W = 844;
export const PALCO_CP_H = 390;

export interface PezziTavolo {
  testata: ReactNode;
  avversario: ReactNode;
  fascia: ReactNode;
  mio: ReactNode;
  mano: ReactNode;
  azioni: ReactNode;
  /** Le righe di stato (errori, attesa): nel tavolo in piedi sotto la testata, nel palco in alto a destra. */
  avvisi?: ReactNode;
  /**
   * La pastiglia del turno («Tocca a te», «Tocca all'avversario», «Connessione…») IN PIEDI: sta SOTTO il riquadro della MIA plancia, centrata (I-807). Nel palco non si passa:
   * lì resta nella `fascia`, sotto gli orologi, nella colonna di destra (sotto la plancia c'e la striscia della mano della Fantasyland, e la plancia arriva a 322 su 390).
   */
  turno?: ReactNode;
  /** Il pannello delle impostazioni, solo nel palco: sostituisce la parte destra (tavola 9). */
  pannello?: ReactNode;
  /** Il resoconto della mano: al posto di avversario, fascia, mio, mano e azioni (testata e avvisi restano). */
  resoconto?: ReactNode;
  /**
   * In piedi: la mano e i tasti restano INCOLLATI in fondo alla zona che scorre (`sticky`), cosi Conferma si raggiunge senza scorrere. Serve perche il tavolo disegnato per 844 px di
   * altezza, con la testata d'app (80) e la barra, ne ha ~635: il banco misura ~900 di contenuto. Si accende solo quando la mano e corta (fino a 5 carte).
   */
  fissaInFondo?: boolean;
  /** Nel palco: la mano (la Fantasyland, oltre 5 carte) sta in una striscia LARGA sotto il tavolo invece che nella colonna di destra, che non la tiene su una fila. */
  manoLarga?: boolean;
  /** Il riquadro «Abbandonare la partita?» nel palco (in piedi e un foglio fuori dal tavolo). */
  confermaPalco?: ReactNode;
}

/**
 * `compatto`: senza il pavimento dei 440 px in piedi (il tutorial divide lo schermo col coach: l'altezza la decide chi lo compone).
 * `denso`: meno vuoto fra i pezzi e attorno al panno (solo il TUTORIAL a 360×640, I-1218; [D] di tma-ui-orcb 15:58Z). Falso di default e la partita non lo passa MAI (un test lo tiene fuori dal tavolo vivo).
 */
export function CpTavoloLayout({ modo, pezzi, compatto = false, denso = false }: { modo: ModoTavolo; pezzi: PezziTavolo; compatto?: boolean; denso?: boolean }) {
  if (modo === 'verticale') {
    return (
      <div data-tavolo-cp data-tavolo-modo="verticale" className={cn('tav-panno flex flex-1 flex-col rounded-[22px] text-fg [overflow-y:clip]', denso ? 'gap-0.5 px-1.5 pb-0.5 pt-0.5' : 'gap-1 px-2 pb-1 pt-1', !compatto && 'min-h-[440px]')}>
        {pezzi.testata}
        {pezzi.avvisi}
        {pezzi.resoconto ?? (
          <>
            {pezzi.avversario}
            {pezzi.fascia}
            {pezzi.mio}
            {pezzi.turno && (
              <div data-riga-turno className="-mt-0.5 flex justify-center">
                {pezzi.turno}
              </div>
            )}
            {pezzi.fissaInFondo ? (
              <div data-fondo-fisso className={cn('sticky bottom-0 z-10 flex flex-col', denso ? '-mx-1.5 gap-1 px-1.5 pb-0.5 pt-0.5' : '-mx-2 gap-1.5 px-2 pb-1 pt-2')} style={{ background: 'linear-gradient(0deg, var(--tav-panno-ombra) 74%, transparent)' }}>
                {pezzi.mano}
                {pezzi.azioni}
              </div>
            ) : (
              <>
                {pezzi.mano}
                {pezzi.azioni}
              </>
            )}
          </>
        )}
      </div>
    );
  }
  const pannello = pezzi.pannello;
  return (
    <div data-tavolo-cp data-tavolo-modo="palco" className="tav-panno relative overflow-hidden rounded-[22px] text-fg" style={{ width: PALCO_CP_W, height: PALCO_CP_H }}>
      <div data-testata-palco className="absolute flex items-center" style={{ left: 28, right: 28, top: 8, height: 36 }}>
        {pezzi.testata}
      </div>
      {pezzi.avvisi && (
        <div data-avvisi-palco className="absolute z-10 flex flex-col gap-1" style={{ left: 300, top: 8, width: 340 }}>
          {pezzi.avvisi}
        </div>
      )}
      {pezzi.confermaPalco}
      {pezzi.resoconto ? (
        pezzi.resoconto
      ) : pannello ? (
        <>
          <div className="absolute opacity-50" style={{ left: 24, top: 52, width: 346 }}>
            {pezzi.mio}
          </div>
          <div data-pannello-palco className="absolute inset-y-0 right-0 border-l border-[color-mix(in_srgb,var(--tav-oro)_30%,transparent)] bg-[#0b3a31]/95" style={{ width: 400 }}>
            {pannello}
          </div>
        </>
      ) : (
        <>
          <div className="absolute" style={{ left: 24, top: 52, width: 262 }}>
            {pezzi.avversario}
          </div>
          <div className="absolute" style={{ left: 296, top: 52, width: 346 }}>
            {pezzi.mio}
          </div>
          <div className="absolute flex flex-col justify-between" style={{ left: 652, top: 52, width: 184, height: 326 }}>
            <div className="flex flex-col gap-3">
              {pezzi.fascia}
              {!pezzi.manoLarga && pezzi.mano}
            </div>
            {pezzi.azioni}
          </div>
          {pezzi.manoLarga && (
            <div data-mano-larga className="absolute" style={{ left: 24, width: 616, top: 322, height: 56 }}>
              {pezzi.mano}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Il tavolo che SCEGLIE il modo. `disegna(modo, cambiaVista)` costruisce i pezzi nel modo dato (le carte hanno taglie diverse) e riceve il gesto che CAMBIA vista: in piedi apre il
 * palco, nel palco torna in piedi (`null` senza schermo). Le righe di stato (`avvisi`) vanno nei pezzi: nel tabellone, che sta sopra a tutto, restano dentro il palco. `onModo` avvisa la rotta
 * del modo in cui si e (le impostazioni nel palco sono un pannello, in piedi un foglio).
 */
export function CpTavoloResponsivo({
  disegna,
  onModo,
}: {
  disegna: (modo: ModoTavolo, cambiaVista: (() => void) | null) => PezziTavolo;
  onModo?: (modo: ModoTavolo) => void;
}) {
  const [orizzontale, setOrizzontale] = useState(false);
  const dispositivo = useOrientamento();
  const imposta = (v: boolean) => {
    setOrizzontale(v);
    onModo?.(v ? 'palco' : 'verticale');
  };
  if (senzaSchermo() || !orizzontale) {
    return <CpTavoloLayout modo="verticale" pezzi={disegna('verticale', senzaSchermo() ? null : () => imposta(true))} />;
  }
  const pezzi = disegna('palco', () => imposta(false));
  return (
    <TabelloneOrizzontale
      ruota={dispositivo === 'verticale'}
      palcoLarghezza={PALCO_CP_W}
      palcoAltezza={PALCO_CP_H}
    >
      <PalcoScalato larghezza={PALCO_CP_W} altezza={PALCO_CP_H}>
        <CpTavoloLayout modo="palco" pezzi={pezzi} />
      </PalcoScalato>
    </TabelloneOrizzontale>
  );
}

const netClass = (n: number): string => (n > 0 ? 'text-emerald-300' : n < 0 ? 'text-rose-300' : 'text-fg-muted');

/**
 * Un punteggio cumulato: l'etichetta corta («Tu», «Avv») piccola e il NUMERO grande, tabulare, col segno (`conSegno`: il meno vero, «+0» a zero) e il colore del netto.
 * Il numero e quello che si guarda; l'etichetta dice di chi e. Etichetta corta (`match.me` / `match.opp`, quella degli orologi): «Avversario» a 11 px accanto a un numero da 20
 * non ci sta in una riga a 360 px.
 */
function CpPunteggio({ chi, etichetta, valore }: { chi: 'io' | 'avversario'; etichetta: string; valore: number }) {
  return (
    <span data-punteggio={chi} className={cn('flex items-baseline gap-1.5 tabular-nums', netClass(valore))}>
      <span className="text-[11px] font-bold uppercase tracking-wide opacity-75">{etichetta}</span>
      <span className="text-[20px] font-extrabold leading-none">{conSegno(valore)}</span>
    </span>
  );
}

/**
 * La testata (tavole 2 e 7, rivista da I-798 fase A): a sinistra «Mano 2» (grande) e «Round 3/5» (piccolo, tabulare), a destra i punteggi cumulati — etichetta piccola, NUMERO grande
 * e colorato — e, in piedi, il tasto che apre il palco (c'e anche nel resoconto). Una riga sola, alta come prima (32 px): in piedi a 360×780 non c'e un pixel da dare.
 * `round: null` = senza il round: col resoconto aperto la testata dice la mano del RESOCONTO e non un round (quello della mano dopo non c'e ancora, e quello della mano di
 * prima non e piu vero: «Mano 2 · Round 5/5» accanto a un resoconto della mano 1 era una testata che contraddice il piede).
 * ⚠️ Il numero TOTALE di mani («Mano 2/6») non si scrive: la vista non lo conosce (arriva solo a fine match, `handsPlayed`, e nel replay REST, `setLength`).
 */
export function CpTestata({ modo, mano, round, io, avv, onCambiaVista }: { modo: ModoTavolo; mano: number; round: number | null; io: number; avv: number; onCambiaVista?: (() => void) | null }) {
  const { t } = useTranslation('chinesepoker');
  const palco = modo === 'palco';
  const punteggi = (
    <div className={cn('flex items-center', palco ? 'gap-4' : 'gap-2.5')}>
      <CpPunteggio chi="io" etichetta={t('match.me')} valore={io} />
      {!palco && <span aria-hidden className="h-5 w-px bg-white/15" />}
      <CpPunteggio chi="avversario" etichetta={t('match.opp')} valore={avv} />
    </div>
  );
  const manoRound = (
    <>
      <span className="text-[18px] font-extrabold text-fg">{t('hand.counter', { n: mano })}</span>
      {round !== null && <span data-round-testata className="text-[12px] tabular-nums text-fg-muted">{t('match.round', { n: round })}</span>}
    </>
  );
  if (palco) {
    return (
      <div className="flex w-full items-center justify-between">
        <span className="flex items-center gap-3">
          {onCambiaVista && <CpTastoVerticale onClick={onCambiaVista} />}
          <span className="flex items-baseline gap-3">{manoRound}</span>
        </span>
        {punteggi}
      </div>
    );
  }
  return (
    <div data-testata-tavolo className="flex h-8 items-center justify-between gap-2 px-2">
      <div className="flex min-w-0 items-baseline gap-2 leading-tight">{manoRound}</div>
      <div className="flex items-center gap-2">
        {punteggi}
        {onCambiaVista && <CpTastoOrizzontale onClick={onCambiaVista} />}
      </div>
    </div>
  );
}

/** L'etichetta sopra una plancia: «AVVERSARIO», le sue pescate e scarti (o la sua Fantasyland), e a destra i tasti (la vista orizzontale). */
export function CpEtichettaPlancia({ nome, dettaglio, badge, destra }: { nome: string; dettaglio?: ReactNode; badge?: ReactNode; destra?: ReactNode }) {
  return (
    <div className="flex min-h-5 items-center gap-2 px-1">
      <span className="tav-etichetta text-[11px] font-bold uppercase tracking-[.16em]">{nome}</span>
      {badge}
      {dettaglio && <span className="min-w-0 truncate text-[12px] text-fg-muted">{dettaglio}</span>}
      {destra && <span className="ml-auto flex shrink-0 items-center gap-1">{destra}</span>}
    </div>
  );
}

/**
 * Gli orologi (due pastiglie, il banco di tempo di ciascuno). In piedi (I-798 fase A) stanno uno accanto all'altro, CENTRATI; nel palco, dove due orologi in linea non stanno nei
 * 184 px della colonna, restano in colonna (centrata), come nelle tavole 2 e 7, e il turno (`CpTurno`) con loro.
 * ⚠️ In piedi la fascia NON ha il pannello di vetro delle tavole: due righe + il suo padding costavano 12 px a 360×780, dove il tavolo ha 604/604 e non ne ha da dare.
 * In piedi la pastiglia del turno NON e piu qui: sta sotto la mia plancia (I-807, `PezziTavolo.turno`). `conTurno` (default: si) la tiene dentro la fascia, come nel palco.
 */
export function CpFascia({ modo, mioTempo, avvTempo, miaMossa, connesso, conTurno = true }: { modo: ModoTavolo; mioTempo: string; avvTempo: string; miaMossa: boolean; connesso: boolean; conTurno?: boolean }) {
  const { t } = useTranslation('chinesepoker');
  const compatta = modo === 'palco';
  const pastiglia = (chiave: 'io' | 'avv', etichetta: string, tempo: string, acceso: boolean) => (
    <span
      data-orologio={chiave}
      className={cn(
        'flex items-center justify-center gap-2 rounded-full font-semibold tabular-nums',
        compatta ? 'px-3 py-1 text-[15px]' : 'min-w-[7.5rem] px-3 py-px text-[16px] leading-5',
        acceso
          ? chiave === 'io'
            ? 'bg-brand/15 text-brand shadow-[inset_0_0_0_1.5px_color-mix(in_srgb,var(--color-brand)_60%,transparent)]'
            : 'bg-black/25 text-[#ecd39a]'
          : 'bg-black/25 text-[#ecd39a]/85',
      )}
    >
      <span className="text-[11px] font-bold uppercase tracking-wide opacity-70">{etichetta}</span>
      {tempo}
    </span>
  );
  const turno = conTurno ? <CpTurno modo={modo} miaMossa={miaMossa} connesso={connesso} /> : null;
  const io = pastiglia('io', t('match.me'), mioTempo, miaMossa);
  const avv = pastiglia('avv', t('match.opp'), avvTempo, !miaMossa);
  if (modo === 'palco') {
    return (
      <div data-fascia className="flex flex-col items-center gap-1.5">
        {io}
        {avv}
        {turno}
      </div>
    );
  }
  return (
    <div data-fascia className="flex flex-col items-center gap-0.5">
      <div data-riga-orologi className="flex items-center justify-center gap-3">
        {io}
        {avv}
      </div>
      {turno}
    </div>
  );
}

/** La pastiglia del turno: «Tocca a te» (teal, il pallino pulsa), «Tocca all'avversario», «Connessione…» (rosso). In piedi sta sotto la mia plancia, nel palco nella fascia. */
export function CpTurno({ modo, miaMossa, connesso }: { modo: ModoTavolo; miaMossa: boolean; connesso: boolean }) {
  const { t } = useTranslation('chinesepoker');
  const compatta = modo === 'palco';
  return (
    <span
      data-turno
      className={cn(
        'flex min-w-0 items-center justify-center gap-2 truncate rounded-full font-semibold',
        compatta ? 'px-3 py-1 text-[14px]' : 'px-3 py-0 text-[13px] leading-4',
        !connesso ? 'bg-danger/15 text-danger' : miaMossa ? 'bg-brand/15 text-brand shadow-[inset_0_0_0_1.5px_color-mix(in_srgb,var(--color-brand)_60%,transparent)]' : 'bg-black/25 text-fg-muted',
      )}
    >
      <span className={cn('h-2.5 w-2.5 rounded-full', !connesso ? 'bg-danger' : miaMossa ? 'animate-pulse bg-brand' : 'bg-fg-muted')} />
      {!connesso ? t('match.connecting') : miaMossa ? t('match.yourTurn') : t('match.opponentTurn')}
    </span>
  );
}

/**
 * I tasti d'azione: «Abbandona» (oro), «Conferma» (teal, il tasto del giocatore) e l'INGRANAGGIO. ⚠️ L'ingranaggio e lo STESSO del burraco: bottone a icona da 40 px,
 * `data-azione="impostazioni"`, in rilievo (`TavoloVerticale.tsx` / `TavoloStage.tsx` di #718). `confermabile` e falso quando non si puo ancora confermare (tasto spento);
 * `puoGiocare` e falso quando non tocca a me: allora «Conferma» non c'e (resta «Abbandona»).
 */
export function CpAzioni({
  modo,
  puoGiocare,
  confermabile,
  onConferma,
  onAbbandona,
  onImpostazioni,
  abbandonabile = true,
  ordina,
  evidenzia = false,
}: {
  modo: ModoTavolo;
  puoGiocare: boolean;
  confermabile: boolean;
  onConferma: () => void;
  onAbbandona?: () => void;
  /** Senza, il tasto delle impostazioni non c'e` (il tutorial non ha preferenze da aprire). */
  onImpostazioni?: () => void;
  abbandonabile?: boolean;
  /** Il tasto «Ordina» (`TastoOrdina`), accanto alla mano: la rotta lo passa solo con almeno 2 carte in mano. */
  ordina?: ReactNode;
  /** Resta solo da scartare (I-798, fase B): Conferma e l'unica cosa da fare, e si vede (anello e alone teal, fermi: il movimento ridotto non cambia niente). */
  evidenzia?: boolean;
}) {
  const { t } = useTranslation('chinesepoker');
  const impostazioni = onImpostazioni === undefined ? null : (
    <Button variant="icona" tono="pieno" size={40} data-azione="impostazioni" aria-label={t('match.impostazioni')} onClick={onImpostazioni} className="tav-tasto-rilievo">
      <Settings aria-hidden size={22} />
    </Button>
  );
  const conferma = puoGiocare ? (
    <Button
      variant="primary"
      data-azione="conferma"
      data-evidenza={evidenzia && confermabile ? 'true' : undefined}
      disabled={!confermabile}
      onClick={onConferma}
      className={cn('tav-cala !px-1 !py-0 text-[15px]', modo === 'palco' ? '!h-10 w-full' : '!h-11 min-w-0 flex-1', evidenzia && confermabile && 'ring-2 ring-white/80 shadow-[0_0_16px_3px_color-mix(in_srgb,var(--color-brand)_70%,transparent)]')}
    >
      {t('match.confirm')}
    </Button>
  ) : null;
  const abbandona = abbandonabile && onAbbandona !== undefined ? (
    <Button variant="secondary" data-azione="abbandona" onClick={onAbbandona} className={cn('tav-scarta min-w-0 flex-1 !px-1 !py-0 text-[15px]', modo === 'palco' ? '!h-10' : '!h-11')}>
      {t('match.resign')}
    </Button>
  ) : null;
  if (modo === 'palco') {
    return (
      <div data-azioni className="flex flex-col gap-1.5">
        {conferma}
        <div className="flex items-center gap-1.5">
          {ordina}
          {impostazioni}
          {abbandona}
        </div>
      </div>
    );
  }
  return (
    <div data-azioni className="flex items-center gap-2 px-1">
      {ordina}
      {impostazioni}
      {abbandona}
      {conferma}
    </div>
  );
}

/**
 * Il tasto che apre il palco orizzontale: stesso nome del burraco (`data-azione="vista-orizzontale"`, 32 px). L'icona e un rettangolo ORIZZONTALE (I-807: la freccia che gira, `RotateCw`,
 * sembrava «ricarica»); la sua gemella nel palco, `Smartphone` in piedi, dice «torna in verticale».
 */
export function CpTastoOrizzontale({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('chinesepoker');
  return (
    <Button variant="icona" size={32} data-azione="vista-orizzontale" aria-label={t('match.vistaOrizzontale')} title={t('match.vistaOrizzontale')} className="!text-[#ecd39a]/85" onClick={onClick}>
      <RectangleHorizontal aria-hidden size={20} />
    </Button>
  );
}

/**
 * Il tasto che TORNA in verticale, nella TESTATA del palco (I-807: «in orizzontale non vedo icone per tornare in verticale»). Prima stava fuori dal palco, appiccicato al bordo
 * sinistro del tabellone (6 px dal bordo del viewport): sotto la tacca e sotto i tasti di Telegram, che il tabellone a schermo intero non scostava. Dentro la testata sta dove sta il
 * palco, sempre dentro i margini sicuri, girato o dritto.
 */
export function CpTastoVerticale({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('chinesepoker');
  return (
    <Button variant="icona" size={32} tono="pieno" data-azione="vista-verticale" aria-label={t('match.vistaVerticale')} title={t('match.vistaVerticale')} className="tav-tasto-rilievo !text-[#ecd39a]" onClick={onClick}>
      <Smartphone aria-hidden size={18} />
    </Button>
  );
}
